import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Autocomplete,
  useTheme,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import InfoIcon from '@mui/icons-material/Info';
import { getPreferredUnits } from "../../../../Services/Global";
import { MultiSectionUnitConfiguration } from "../MultiSectionUnitConfiguration";
import { MultiSectionFlowchart } from "../../../Atom/MultiSectionVisualization/MultiSectionFlowchart";
import { IMultiSectionUnitConfiguration, IUnitBaseConfiguration } from "../../../../Models/IUnitConfiguration";
import { PropertyConstants } from "../../../../Models/Constants";
import { InputOutputOption, PHETypeOption } from './UnitConfigurationEditorTypes';
import { createFlexStyles, createSpacingStyles, createContentStyles, createCardStyles } from '../../../../theme/layoutStyles';

/**
 * Loading indicator component
 */
export const LoadingIndicator: React.FC = () => {
  const theme = useTheme();
  const contentStyles = createContentStyles(theme);
  
  return (
    <Box sx={contentStyles.loadingState}>
      <CircularProgress size={40} />
      <Typography variant="body1" ml={2}>
        Loading...
      </Typography>
    </Box>
  );
};

/**
 * Action buttons component for saving and navigating
 */
export const ActionButtons: React.FC<{
  isSaving: boolean;
  saveMessage?: string;
  onSave: () => void;
  onPropertySpec: () => void;
  onPropertyRangeSpec: () => void;
  isNewCreated: boolean;
  isEdit: boolean;
}> = ({ 
  isSaving, 
  saveMessage, 
  onSave, 
  onPropertySpec, 
  onPropertyRangeSpec,
  isNewCreated,
  isEdit
}) => {
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  
  return (
    <Box sx={{
      ...flexStyles.flexCenter,
      justifyContent: 'flex-end', // Override for right alignment
      gap: theme.spacing(1)
    }}>
      {isSaving && (
        <Box sx={{ ...flexStyles.flexCenter, mr: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Saving...</Typography>
        </Box>
      )}
      
      {saveMessage && (
        <Typography variant="body2" sx={{ mr: 2 }}>
          {saveMessage}
        </Typography>
      )}
      
      <Button
        variant="contained"
        color="primary"
        startIcon={<SaveIcon />}
        onClick={onSave}
        disabled={isSaving}
        sx={{
          whiteSpace: 'nowrap', 
          padding: '6px 26px !important',
          textTransform: 'none',
          boxShadow: 1
        }}
      >
        Save
      </Button>
      
      <Button
        variant="contained"
        color="primary"
        startIcon={<EqualizerIcon />}
        onClick={onPropertySpec}
        sx={{
          whiteSpace: 'nowrap', 
          padding: '6px 26px !important',
          textTransform: 'none',
          boxShadow: 1
        }}
      >
        Property specification
      </Button>
      
      <Button
        variant="contained"
        color="primary"
        startIcon={<InfoIcon />}
        disabled={isSaving || (!isNewCreated && !isEdit)}
        onClick={onPropertyRangeSpec}
        sx={{
          whiteSpace: 'nowrap', 
          padding: '6px 26px !important',
          textTransform: 'none',
          boxShadow: 1
        }}
      >
        Property range specification
      </Button>
    </Box>
  );
};

/**
 * Section buttons component for selecting the number of sections
 */
export const SectionButtons: React.FC<{
  maxSections: number;
  currentSections: number;
  onSetSectionCount: (count: number) => void;
}> = ({ maxSections, currentSections, onSetSectionCount }) => {
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  
  const buttons: JSX.Element[] = [];
  for (let i = 0; i < maxSections; i++) {
    buttons.push(
      <Button
        key={`section-btn-${i+1}`}
        variant={i + 1 === currentSections ? "contained" : "outlined"}
        color="primary"
        onClick={() => onSetSectionCount(i + 1)}
        sx={{
          minWidth: '48px',
          minHeight: '40px',
          textTransform: 'none',
          fontWeight: 'bold',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 2
          }
        }}
      >
        {i + 1}
      </Button>
    );
  }
  
  return (
    <Box sx={{
      ...flexStyles.flexCenter,
      gap: theme.spacing(1.5),
      margin: theme.spacing(2, 0)
    }}>
      {buttons}
    </Box>
  );
};

/**
 * BaseConfigurationForm component for editing basic configuration properties
 */
export const BaseConfigurationForm: React.FC<{
  unitConfig: IUnitBaseConfiguration;
  pheTypesOptions: PHETypeOption[];
  assignOperationTargetValue: boolean;
  isEdit: boolean;
  sectionCount: number;
  onBaseChange: (property: string, value: string | boolean) => void;
  onPHETypeChange: (newSelection: string[]) => void;
  onOperationTargetValueChange: (checked: boolean) => void;
}> = ({
  unitConfig,
  pheTypesOptions,
  assignOperationTargetValue,
  isEdit,
  sectionCount,
  onBaseChange,
  onPHETypeChange,
  onOperationTargetValueChange
}) => {
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  const spacingStyles = createSpacingStyles(theme);
  const cardStyles = createCardStyles(theme);
  
  const disableSlashKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const disabledKeys = ['/'];
    if (disabledKeys.includes(event.key)) {
      event.preventDefault();
    }
  };
  
  return (
    <Paper elevation={2} sx={{
      ...cardStyles.sectionCard,
      ...spacingStyles.marginBottom3,
      width: '100%'
    }}>
      {/* First row */}
      <Box sx={{
        ...flexStyles.flexCenter,
        gap: theme.spacing(2),
        ...spacingStyles.marginBottom2
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Industry
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={unitConfig?.industry ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.Industry, e.target.value)}
          />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            SubIndustry
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={unitConfig?.subIndustry ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.SubIndustry, e.target.value)}
          />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Application
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={unitConfig?.application ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.Application, e.target.value)}
          />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Subapplication
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={unitConfig?.subApplication ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.SubApplication, e.target.value)}
          />
        </Box>
      </Box>
      
      {/* Second row */}
      <Box sx={{
        ...flexStyles.flexCenter,
        gap: theme.spacing(2),
        ...spacingStyles.marginBottom2
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Process stage
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={unitConfig?.processStage ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.ProcessStage, e.target.value)}
          />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Product line
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={unitConfig?.productLine || ""}
              onChange={(e) => onBaseChange(PropertyConstants.ProductLine, e.target.value)}
            >
              <MenuItem value="Hygienic">Hygienic</MenuItem>
              <MenuItem value="Industrial">Industrial</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Alfa laval position
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={unitConfig?.alfaLavalPosition ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.AlfaLavalPosition, e.target.value)}
          />
        </Box>
      </Box>
      
      {/* Third row */}
      <Box sx={{
        ...flexStyles.flexCenter,
        gap: theme.spacing(2),
        ...spacingStyles.marginBottom2
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Flow type
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={unitConfig?.flowType || ""}
              onChange={(e) => onBaseChange(PropertyConstants.FlowType, e.target.value)}
            >
              <MenuItem value="mass">
                Mass ({getPreferredUnits().preferredUnits === "us" ? "lb/h" : "kg/h"})
              </MenuItem>
              <MenuItem value="volumetric">
                Volumetric ({getPreferredUnits().preferredUnits === "us" ? "GPM" : "m3/h"})
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Allowed PHE Types
          </Typography>
          <Autocomplete
            multiple
            options={pheTypesOptions}
            getOptionLabel={(option) => option.text}
            value={pheTypesOptions.filter(option => {
              const dbPheTypes = unitConfig?.pheTypes || [];
              return dbPheTypes.includes(option.key);
            })}
            onChange={(e, values) => {
              onPHETypeChange(values.map(v => v.key));
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.key}
                  label={option.text}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))
            }
            size="small"
            disableCloseOnSelect
            sx={{
              '& .MuiOutlinedInput-root': {
                // Nieaktywny: tylko 1 rząd
                maxHeight: '40px',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease',
                
                // Aktywny: pełna wysokość
                '&.Mui-focused, &:focus-within': {
                  maxHeight: 'none',
                  overflow: 'visible'
                }
              },
              '& .MuiChip-root': {
                height: '24px',
                marginTop: '3px',
                marginBottom: '5px',

              },
            }}
          />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Operation target name
          </Typography>
          <TextField
            fullWidth
            size="small"
            disabled={isEdit}
            value={unitConfig?.unitOperationTargetName ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.UnitOperationTargetName, e.target.value)}
            sx={{background: '#f3f2f1'}}
          />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Operation target unit
          </Typography>
          <TextField
            fullWidth
            size="small"
            disabled={isEdit}
            value={unitConfig?.unitOperationTargetValueUnit ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.UnitOperationTargetValueUnit, e.target.value)}
            sx={{background: '#f3f2f1'}}
          />
        </Box>
        
        {sectionCount === 1 && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Assign operation target value
            </Typography>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={assignOperationTargetValue ?? false}
                  onChange={(e) => onOperationTargetValueChange(e.target.checked)}
                />
              }
              label=""
            />
          </Box>
        )}
      </Box>
      
      {/* Fourth row */}
      <Box sx={{
        ...flexStyles.flexCenter,
        gap: theme.spacing(2),
        ...spacingStyles.marginBottom2
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Model endpoint URL
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={unitConfig?.modelEndpointUrl ?? ""}
            onChange={(e) => onBaseChange(PropertyConstants.ModelEndpointUrl, e.target.value)}
          />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Model api key name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={unitConfig?.modelApiKeyName ?? ""}
            onKeyPress={disableSlashKey}
            onChange={(e) => onBaseChange(PropertyConstants.ModelApiKeyName, e.target.value)}
          />
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * SectionConfigurationView component for displaying multi-section configuration
 */
export const SectionConfigurationView: React.FC<{
  sectionCollection: IMultiSectionUnitConfiguration[];
  unitConfig: IUnitBaseConfiguration;
  flowchartSelectableProperties: string[];
  isEdit: boolean;
  isNewCreated: boolean;
  isLoading: boolean;
  onSectionChange: (property: string, value: any, index: number) => void;
  onSetActiveSection: (section: number) => void;
  inputOutputOptions: InputOutputOption[];
}> = ({
  sectionCollection,
  unitConfig,
  flowchartSelectableProperties,
  isEdit,
  isNewCreated,
  isLoading,
  onSectionChange,
  onSetActiveSection,
  inputOutputOptions
}) => {
  const theme = useTheme();
  
  // Always ensure at least one section exists
  const sections = sectionCollection && sectionCollection.length > 0 
    ? sectionCollection 
    : [{ sectionNumber: 1, inputs: [], outputs: [] }];
  
  return (
    <Paper sx={{ 
        boxShadow: 'none',
     width: '100%' }}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Section Configuration
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        gap: 2,
        justifyContent: 'flex-start'
      }}>
        {sections
          .sort((a, b) => (b.sectionNumber || 0) - (a.sectionNumber || 0))
          .map((section, index) => (
            <Box 
              key={`sectionconfig-${index}`} 
              sx={{ 
                flex: {
                  xs: '1 1 100%',      // Full width on mobile
                  sm: '1 1 calc(50% - 16px)',  // 2 per row on small screens with gap
                  md: sections.length > 3 ? '1 1 calc(33.333% - 16px)' : '1 1 calc(50% - 16px)', // 3 per row on medium if more than 3 sections
                  lg: sections.length > 4 ? '1 1 calc(20% - 16px)' : sections.length > 3 ? '1 1 calc(25% - 16px)' : '1 1 calc(33.333% - 16px)' // Up to 5 per row on large screens
                },
                minWidth: '250px',
                maxWidth: '100%'
              }}
            >
              <MultiSectionUnitConfiguration
                displayIndex={section.sectionNumber || 1}
                section={section}
                inputOutputOptions={inputOutputOptions}
                onChange={onSectionChange}
                onClick={onSetActiveSection}
                index={section.sectionNumber || 1} 
              />
            </Box>
          ))}
      </Box>
      
      {sections.length > 1 && flowchartSelectableProperties.length > 0 && (
        <Box 
          sx={{
            display: 'flex',
            justifyContent: 'stretch',
            width: '100%',
            marginTop: theme.spacing(4),
            padding: theme.spacing(2),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.paper,
            overflow: 'hidden',
            '& > *': { width: '100%' }
          }} 
          onClick={() => onSetActiveSection(-1)}
        >
          <MultiSectionFlowchart
            unitInputConfiguration={unitConfig!}
            isEdit={isEdit}
            isNewCreated={isNewCreated}
            sectionCount={sections.length}
            selectableProperties={flowchartSelectableProperties}
            scaling={1} 
          />
        </Box>
      )}
    </Paper>
  );
}; 